import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { endpoint } from "../../utils/endpoints";
import { normalizeSearchInput, SEARCH_MAX_LENGTH } from "../../utils/searchInput";
import { hasCsvLinkInResponse } from "../../utils/csvExportSocket";
import { downloadCsvLogFile, isCsvLogDownloadReady } from "../../utils/driverLedgerCsv";
import {
  fetchPaymentCsvLog,
  PAYMENT_CSV_EXPORT_KEY,
  requestPaymentHistoryCsvExport,
} from "../../utils/paymentHistoryCsv";
import { useCsvExportSocket } from "../../context/CsvExportSocketContext";
import DateRangeFilter from "../../Components/DateRangeFilter/DateRangeFilter";
import DateRangeAppliedLabel from "../../Components/DateRangeFilter/DateRangeAppliedLabel";
import { parseApiDate, toApiDate, validateDateRange } from "../../utils/dateRange";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import DownloadIcon from "../../assets/image/download.svg";
import CsvLogStatusBadge from "../../Components/CsvLogStatusBadge/CsvLogStatusBadge";
import { formatDateTime } from "../../utils/formatDateTime";
import { useBackToList } from "../../hooks/useBackToList";
import { formatInr as formatINR } from "../../utils/formatAmount";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

const C = {
  bg: "#FFFFFF",
  border: "#E5E7EB",
  text: "#111827",
  muted: "#6B7280",
  primary: "#000053",
  tableHead: "#000053",
};

const ALL_TYPES = "All Types";

/** Payment history type filter — API `type` query values. */
const PAYMENT_TYPE_FILTERS = [
  { value: "normal advance", label: "Normal advance" },
  { value: "salary amount", label: "Salary amount" },
  { value: "award amount", label: "Award amount" },
];

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

const toAmountNumber = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const truncateWords = (value, maxWords = 50) => {
  const text = String(value ?? "").trim();
  if (!text) return "—";
  const words = text.split(/\s+/);
  if (words.length <= maxWords) return text;
  return `${words.slice(0, maxWords).join(" ")}...`;
};

const downloadBlobFile = (filename, blob) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const mapPaymentRow = (item) => {
  const salary = toAmountNumber(item.salary_amount);
  const award = toAmountNumber(item.award_amount);
  const advance = toAmountNumber(item.normal_advance);
  const tripAdvance = toAmountNumber(item.trip_advance_amount);
  const amountPaid = salary + award + advance + tripAdvance;
  const note = String(item.note ?? "").trim();

  let reference = "—";
  if (item.trip_ref_id != null && item.trip_ref_id !== "") {
    reference = String(item.trip_ref_id);
  } else if (item._id) {
    reference = String(item._id).slice(-8).toUpperCase();
  }

  return {
    id: item._id,
    dateTime: formatDateTime(item.created_at || item.updated_at),
    driverName: item.full_name ?? "—",
    amountPaid: formatINR(amountPaid),
    verificationType: item.type ?? "—",
    accountantName: item.accountant_name ?? "—",
    employeeId: item.employee_id ?? "—",
    transactionType: item.transition_type ?? "—",
    note: truncateWords(note),
    fullNote: note || "—",
    reference,
    previousBalance: formatINR(item.previous_balance),
    newBalance: formatINR(item.new_balance),
  };
};

const ViewTransactions = () => {
  const { setSubheading } = useOutletContext();
  const backToList = useBackToList("/salary-disbursement");
  const { trackExport, clearExport, registerExportHandler, isExportPending, finishExport } =
    useCsvExportSocket();
  const exportKey = PAYMENT_CSV_EXPORT_KEY;
  const [activeTab, setActiveTab] = useState("listing");

  useEffect(() => {
    setSubheading("Payment History");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pickerRange, setPickerRange] = useState([null, null]);
  const [type, setType] = useState(ALL_TYPES);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const dateRangeCheck = validateDateRange(fromDate, toDate);
  const dateValidationError = dateRangeCheck.valid ? "" : dateRangeCheck.message;
  const hasDateRangeSelected = Boolean(fromDate && toDate);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [apiTotalPages, setApiTotalPages] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [csvLogRows, setCsvLogRows] = useState([]);
  const [csvLogLoading, setCsvLogLoading] = useState(false);
  const [downloadingLogId, setDownloadingLogId] = useState(null);

  const fetchRequestIdRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPickerRange([parseApiDate(fromDate), parseApiDate(toDate)]);
  }, [fromDate, toDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, fromDate, toDate, type]);

  const fetchPaymentHistory = useCallback(
    async ({ useDateFilter = false, page = currentPage } = {}) => {
      const requestId = ++fetchRequestIdRef.current;
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        if (requestId === fetchRequestIdRef.current) {
          setRows([]);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(itemsPerPage),
      });
      const q = debouncedSearch.trim();
      if (q) params.set("search", q);
      if (type && type !== ALL_TYPES) params.set("type", type);
      if (useDateFilter) {
        params.set("from_date", fromDate);
        params.set("to_date", toDate);
      }

      const url = `${ACCOUNTANT_API}${endpoint.paymentHistory}?${params.toString()}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await parseBody(response);
        if (requestId !== fetchRequestIdRef.current) return;

        if (!response.ok) {
          toast.error(getErrorMessage(payload, response));
          setRows([]);
          setTotalRecords(0);
          setApiTotalPages(1);
          return;
        }

        const apiCode = payload?.code;
        if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
          toast.error(getErrorMessage(payload, response));
          setRows([]);
          setTotalRecords(0);
          setApiTotalPages(1);
          return;
        }

        const data = payload?.data || {};
        const list = Array.isArray(data.payment_history_data) ? data.payment_history_data : [];
        const parsedTotal = Number(data.total);
        const total =
          Number.isFinite(parsedTotal) && parsedTotal >= 0 ? parsedTotal : list.length;
        const totalPagesFromApi =
          typeof data.total_pages === "number" && data.total_pages > 0 ? data.total_pages : 1;

        setRows(list.map(mapPaymentRow));
        setTotalRecords(total);
        setApiTotalPages(totalPagesFromApi);
        if (page > totalPagesFromApi) {
          setCurrentPage(totalPagesFromApi);
        }
      } catch (err) {
        if (requestId !== fetchRequestIdRef.current) return;
        console.error(err);
        toast.error(
          err?.message === "Failed to fetch"
            ? "Network error. Check your connection."
            : "Could not load payment history."
        );
        setRows([]);
        setTotalRecords(0);
        setApiTotalPages(1);
      } finally {
        if (requestId === fetchRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [currentPage, itemsPerPage, debouncedSearch, type, fromDate, toDate]
  );

  useEffect(() => {
    if (dateValidationError) {
      setRows([]);
      setLoading(false);
      setTotalRecords(0);
      setApiTotalPages(1);
      return undefined;
    }

    fetchPaymentHistory({
      useDateFilter: hasDateRangeSelected,
      page: currentPage,
    });
    return undefined;
  }, [
    fetchPaymentHistory,
    fromDate,
    toDate,
    type,
    debouncedSearch,
    currentPage,
    dateValidationError,
    hasDateRangeSelected,
  ]);

  const fetchCsvLog = useCallback(async () => {
    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    setCsvLogLoading(true);
    try {
      const rows = await fetchPaymentCsvLog(token);
      setCsvLogRows(rows);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : err?.message || "Could not load CSV log."
      );
      setCsvLogRows([]);
    } finally {
      setCsvLogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "logCsv") {
      fetchCsvLog();
    }
  }, [activeTab, fetchCsvLog]);

  useEffect(() => {
    return registerExportHandler(exportKey, {
      onComplete: () => {
        fetchCsvLog();
        setActiveTab("logCsv");
        setExporting(false);
      },
      onError: () => setExporting(false),
    });
  }, [exportKey, registerExportHandler, fetchCsvLog]);

  const handleApplyDates = () => {
    const [start, end] = pickerRange;
    if (!start || !end) {
      toast.error("Please select start and end dates");
      return false;
    }
    setFromDate(toApiDate(start));
    setToDate(toApiDate(end));
    setCurrentPage(1);
    return true;
  };

  const handleClearDates = () => {
    setPickerRange([null, null]);
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
  };

  const clearListingFilters = () => {
    handleClearDates();
    setType(ALL_TYPES);
    setSearch("");
    setCurrentPage(1);
  };

  const hasAnyListingFilter = Boolean(
    hasDateRangeSelected ||
      (type && type !== ALL_TYPES) ||
      search.trim() ||
      pickerRange[0] ||
      pickerRange[1]
  );

  const hasBothDates = hasDateRangeSelected;

  const handleExportCsv = useCallback(async () => {
    if (totalRecords <= 0) {
      toast.info("No transactions available to export.");
      return;
    }

    setExporting(true);
    trackExport(exportKey);
    try {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        clearExport(exportKey);
        setExporting(false);
        return;
      }

      const exportType = type && type !== ALL_TYPES ? type : undefined;
      const payload = await requestPaymentHistoryCsvExport({
        search: debouncedSearch.trim(),
        fromDate: hasBothDates ? fromDate : "",
        toDate: hasBothDates ? toDate : "",
        type: exportType,
        token,
      });

      if (hasCsvLinkInResponse(payload)) {
        finishExport(exportKey, { payload });
        return;
      }
    } catch (err) {
      console.error(err);
      clearExport(exportKey);
      setExporting(false);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : err?.message || "Could not submit export request."
      );
    }
  }, [
    fromDate,
    toDate,
    hasBothDates,
    debouncedSearch,
    type,
    totalRecords,
    trackExport,
    clearExport,
    finishExport,
    exportKey,
  ]);

  const handleDownloadCsvLog = useCallback(async (row) => {
    if (!isCsvLogDownloadReady(row)) {
      toast.info(
        row?.statusLabel === "Failed"
          ? "CSV export failed. Please try exporting again."
          : "File is not available for download yet."
      );
      return;
    }

    setDownloadingLogId(row.id);
    try {
      const result = await downloadCsvLogFile(row);
      if (result?.blob) {
        downloadBlobFile(result.filename, result.blob);
      } else if (result === null) {
        toast.info("Opening download in a new tab.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Could not download file.");
    } finally {
      setDownloadingLogId(null);
    }
  }, []);

  const exportInProgress = exporting || isExportPending(exportKey);
  const totalPages = apiTotalPages;
  const page = Math.min(currentPage, totalPages);
  const firstIndex = (page - 1) * itemsPerPage;
  const canPaginate = !loading && !dateValidationError;
  const canExport =
    !exportInProgress &&
    !dateValidationError &&
    !loading &&
    totalRecords > 0;

  return (
    <div className="tab-pane-content mt-7 font-UbuntuRegular">
      <div className="flex justify-start">
        <Link to={backToList} className="btn btn-primary">
          Back
        </Link>
      </div>

      <div className="flex flex-wrap gap-3 mt-5 mb-6">
        <button
          type="button"
          onClick={() => setActiveTab("listing")}
          className="px-6 py-2.5 text-sm font-UbuntuMedium rounded-md border"
          style={{
            borderColor: C.border,
            background: activeTab === "listing" ? C.primary : "#fff",
            color: activeTab === "listing" ? "#fff" : C.primary,
          }}
        >
          Listing
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("logCsv")}
          className="px-6 py-2.5 text-sm font-UbuntuMedium rounded-md border"
          style={{
            borderColor: C.border,
            background: activeTab === "logCsv" ? C.primary : "#fff",
            color: activeTab === "logCsv" ? "#fff" : C.primary,
          }}
        >
          Log CSV
        </button>
      </div>

      {activeTab === "listing" ? (
        <>
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3 mb-4">
        <div className="shrink-0">
          <label className="text-xs font-UbuntuMedium block mb-1.5" style={{ color: C.muted }}>
            Date range
          </label>
          <div className="inline-flex h-11 flex-nowrap items-center gap-2.5">
            <DateRangeFilter
              variant="popover"
              bare
              toolbarIcon
              range={pickerRange}
              onRangeChange={setPickerRange}
              onApply={handleApplyDates}
              onClear={handleClearDates}
              maxDate={new Date()}
            />
            {hasDateRangeSelected && (
              <DateRangeAppliedLabel
                from={fromDate}
                to={toDate}
                prefix="Date range: "
                className="shrink-0 whitespace-nowrap"
              />
            )}
          </div>
        </div>

        {hasAnyListingFilter ? (
          <button
            type="button"
            onClick={clearListingFilters}
            className="h-11 shrink-0 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-4 text-sm font-UbuntuMedium text-gray-800 shadow-sm hover:border-gray-400 hover:bg-gray-50"
          >
            Clear Filters
          </button>
        ) : null}

        <div className="min-w-[180px]">
          <label className="text-xs font-UbuntuMedium block mb-1.5" style={{ color: C.muted }}>
            Transition Type
          </label>
          <select
            className="form-control h-11 rounded-lg border"
            style={{ borderColor: C.border }}
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value={ALL_TYPES}>{ALL_TYPES}</option>
            {PAYMENT_TYPE_FILTERS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-[220px]">
          <label className="text-xs font-UbuntuMedium block mb-1.5" style={{ color: C.muted }}>
            Search Driver
          </label>
          <input
            type="text"
            className="border px-3 py-2.5 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm w-full h-11"
            style={{ borderColor: C.border }}
            placeholder="Search by name or ID..."
            value={search}
            maxLength={SEARCH_MAX_LENGTH}
            onChange={(e) => {
              setSearch(normalizeSearchInput(e.target.value));
              setCurrentPage(1);
            }}
          />
        </div>

        <button
          type="button"
          onClick={handleExportCsv}
          disabled={!canExport}
          className="ml-auto h-11 px-8 rounded-md text-white font-UbuntuMedium !bg-primary disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: C.primary }}
        >
          {exportInProgress ? "Exporting..." : "Export"}
        </button>
      </div>

      {dateValidationError ? (
        <p className="mb-3 text-sm text-red-500">{dateValidationError}</p>
      ) : null}

      <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.border }}>
        <div className="table-responsive m-0">
          <table className="w-full mb-0 bg-white">
            <thead>
              <tr className="text-center" style={{ background: C.tableHead }}>
                {[
                  "S.No.",
                  "Date & Time",
                  "Driver Name",
                  "Amount Paid",
                  "Verification Type",
                  "Accountant Name",
                  "Employee ID",
                  "Transaction Type",
                  "Reference",
                  "Previous Balance",
                  "New Balance",
                  "Note",
                ].map((title) => (
                  <th
                    key={title}
                    className="text-[13px] font-UbuntuMedium text-white whitespace-nowrap px-3 py-3.5 border-0"
                  >
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={12} className="text-center py-10" style={{ color: C.muted }}>
                    Loading transactions…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center py-10" style={{ color: C.muted }}>
                    No transactions found.
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={row.id} className={i % 2 ? "bg-[#FAFAFA] text-center" : "bg-white text-center  "}>
                    <td className="px-3 py-4 text-center  ">{String(firstIndex + i + 1).padStart(2, "0")}</td>
                    <td className="px-3 py-4 whitespace-nowrap text-center">{row.dateTime}</td>
                    <td className="px-3 py-4 text-center">{row.driverName}</td>
                    <td className="px-3 py-4 text-center">{row.amountPaid}</td>
                    <td className="px-3 py-4 text-center">{row.verificationType}</td>
                    <td className="px-3 py-4 text-center whitespace-nowrap">{row.accountantName}</td>
                    <td className="px-3 py-4 text-center whitespace-nowrap">{row.employeeId}</td>
                    <td className="px-3 py-4 text-center">{row.transactionType}</td>
                    <td className="px-3 py-4 text-center whitespace-nowrap">{row.reference}</td>
                    <td className="px-3 py-4 text-center">{row.previousBalance}</td>
                    <td className="px-3 py-4 text-center">{row.newBalance}</td>
                    <td
                      className="px-3 py-4 min-w-[260px] max-w-[360px] whitespace-pre-wrap break-words text-center leading-relaxed"
                      title={row.fullNote}
                    >
                      {row.note}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 mt-5 text-lg">
        <span style={{ color: C.muted }}>
          {totalRecords.toLocaleString("en-IN")} records found
        </span>
        <button
          type="button"
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={page === 1 || !canPaginate}
          className="!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center"
        >
          <img src={LeftAngle} className="invert grayscale brightness-100 h-3" alt="" />
        </button>
        <PageNumberInput
          page={page}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          disabled={!canPaginate}
          style={{ borderColor: C.border, color: C.text }}
        />
        <span style={{ color: C.muted }}>of {totalPages}</span>
        <button
          type="button"
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={page === totalPages || !canPaginate}
          className="!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center"
        >
          <img src={RightAngle} className="invert grayscale brightness-100 h-3" alt="" />
        </button>
      </div>
      </>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.border }}>
          <div className="table-responsive m-0">
            <table className="w-full mb-0 bg-white">
              <thead>
                <tr className="text-center" style={{ background: C.tableHead }}>
                  {["S.No.", "File Name", "Filters", "Created Date", "Status", "Action"].map((title) => (
                    <th
                      key={title}
                      className="text-[13px] font-UbuntuMedium text-white whitespace-nowrap px-3 py-3.5 border-0"
                    >
                      {title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvLogLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10" style={{ color: C.muted }}>
                      Loading CSV log…
                    </td>
                  </tr>
                ) : csvLogRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10" style={{ color: C.muted }}>
                      No CSV log entries found.
                    </td>
                  </tr>
                ) : (
                  csvLogRows.map((row, i) => (
                    <tr
                      key={row.id}
                      className={i % 2 ? "bg-[#FAFAFA] text-center" : "bg-white text-center"}
                    >
                      <td className="px-3 py-4">{String(i + 1).padStart(2, "0")}</td>
                      <td className="px-3 py-4 max-w-[240px] truncate" title={row.fileName}>
                        {row.fileName}
                      </td>
                      <td className="px-3 py-4 max-w-[280px] whitespace-normal text-center" title={row.filters}>
                        {row.filters || "—"}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">{row.createdAt}</td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <CsvLogStatusBadge statusLabel={row.statusLabel} />
                      </td>
                      <td className="px-3 py-4">
                        <button
                          type="button"
                          onClick={() => handleDownloadCsvLog(row)}
                          disabled={!isCsvLogDownloadReady(row) || downloadingLogId === row.id}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ background: C.tableHead }}
                          title="Download"
                          aria-label="Download"
                        >
                          <img src={DownloadIcon} alt="" className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewTransactions;
